// Lens Module
import Vue from 'vue'
import LensAPI from '@/api/lens'
import _ from 'lodash'
import lineColors from '../../utils/lens/selectedMetricColors'
import store from '@/store'
import FirebaseAPI from '@/api/firebase'
import { isWithinDaysAgo } from '../../utils/dateUtils'

export default {
  namespaced: true,
  state: {
    activeLensId: null,

    columnDrawerView: false,
    presets: [],
    lenses: [],
    sidebarLenses: [],
    lensFolders: {}, // key: lensId, value: [folders]
    isCustomPreset: false,
    colorFormat: '',

    mappedRowColors: {
      bar: {},
      line: {}
    },

    // Pagination
    pagination: {
      currentPage: 1,
      limit: 10,
      totalItems: 0,
      totalPages: 0
    },
    /**
     * selectedPreset - source of truth, used to populate local state
     * appliedPreset - used for the current session modifications
     * localPreset - local preset for saving, apply changes to applied & selected
     */
    selectedPreset: {}, // Seemingly unused...
    appliedPreset: {},
    localPreset: {},
    showLensCreationModal: false,
    showReportCreationModal: null, // Will be set to init folder id
    showReportShareModal: false,

    // If a report is updated/deleted in sidebar, we need to update the active page (if applicable)
    pendingLocalReportUpdate: null,

    fbConnectionStatus: {},
    lensConnectionsStatus: {},

    showEditTagModal: false,
    tags: []
  },
  getters: {
    getActiveLensId: state => state.activeLensId,
    getPresets: state => state.presets,
    getLenses: state => state.lenses,
    getSidebarLenses: state => state.sidebarLenses,
    getLensFolders: state => state.lensFolders,
    getSelectedPreset: state => state.selectedPreset,
    getAppliedPreset: state => state.appliedPreset,
    getLocalPreset: state => state.localPreset,
    getColumnDrawerView: state => state.columnDrawerView,
    getIsCustomPreset: state => state.isCustomPreset,
    getColorFormat: state => state.colorFormat,
    getPagination: state => state.pagination,
    getShowLensCreationModal: state => state.showLensCreationModal,
    getShowReportCreationModal: state => state.showReportCreationModal,
    getShowReportShareModal: state => state.showReportShareModal,
    getMappedRowColor: state => state.mappedRowColors,
    getPendingLocalReportUpdate: state => state.pendingLocalReportUpdate,
    getFBConnectionStatus: state => state.fbConnectionStatus,
    getLensConnectionsStatus: state => state.lensConnectionsStatus,
    getLensTags: state => state.tags,
    getShowEditTagModal: state => state.showEditTagModal
  },
  mutations: {
    SET_ACTIVE_LENS_ID (state, lensId) {
      state.activeLensId = lensId
    },
    SET_PRESETS (state, presets) {
      state.presets = presets
    },
    SET_LENSES (state, lenses) {
      state.lenses = lenses
    },
    SET_SIDEBAR_LENSES (state, lenses) {
      state.sidebarLenses = lenses
    },
    SET_LENS_FOLDERS (state, lensFolders) {
      state.lensFolders = { ...lensFolders }
    },
    SET_SELECTED_PRESET (state, preset) {
      state.selectedPreset = { ...preset }
    },
    SET_APPLIED_PRESET (state, preset) {
      state.appliedPreset = { ...preset }
    },
    SET_LOCAL_PRESET (state, preset) {
      state.localPreset = { ...preset }
    },
    SET_PRESET_VALUE (state, { key, value }) {
      Vue.set(state.appliedPreset, key, value)
    },
    SET_LOCAL_PRESET_VALUE (state, { key, value }) {
      Vue.set(state.localPreset, key, value)
    },
    SET_COLUMN_DRAWER_VIEW (state, canShow) {
      state.columnDrawerView = canShow
    },
    SET_IS_CUSTOM_PRESET (state, isCustom) {
      state.isCustomPreset = isCustom
    },
    SET_COLOR_FORMAT (state, colorFormat) {
      state.colorFormat = colorFormat
    },
    SET_PAGINATION (state, pagination) {
      state.pagination = { ...pagination }
    },
    SET_SHOW_LENS_CREATION_MODAL (state, show) {
      state.showLensCreationModal = show
    },
    SET_SHOW_REPORT_CREATION_MODAL (state, folderId) {
      state.showReportCreationModal = folderId
    },
    SET_SHOW_REPORT_SHARE_MODAL (state, show) {
      state.showReportShareModal = show
    },
    SET_MAPPED_ROW_COLOR (state, data) {
      const { newMappedColors, graph } = data
      if (graph) {
        state.mappedRowColors[graph] = newMappedColors
      } else {
        state.mappedRowColors = newMappedColors
      }
    },
    SET_PENDING_LOCAL_REPORT_UPDATE (state, update) {
      state.pendingLocalReportUpdate = update
    },
    SET_FB_CONNECTION_STATUS (state, connectionStatus) {
      state.fbConnectionStatus = connectionStatus
    },
    SET_LENS_CONNECTIONS_STATUS (state, connectionStatuses) {
      state.lensConnectionsStatus = connectionStatuses
    },
    SET_TAGS (state, tags) {
      state.tags = tags
    },
    SET_SHOW_EDIT_TAG_MODAL (state, show) {
      state.showEditTagModal = show
    }
  },
  actions: {
    async fetchPresets ({ state, commit }, lensId = null) {
      console.log('%c Fetching Presets', 'font-size: 14px; color: pink')

      const reqLensId = lensId ?? (state.activeLensId ?? null)
      const res = await LensAPI.LensPreset.getAll(reqLensId)

      // Merge in the lens-specific metrics for each preset (if applicable)
      const presets = res.data.map(preset => {
        const tableColumns = [...preset.table_columns]
        const lensSpecificColumns = [...(preset?.lens_specific_columns?.[reqLensId] ?? [])]
        lensSpecificColumns.forEach(({ key, index }) => {
          // Attempt to insert the lens-specific column into its specified index
          tableColumns.splice(index, 0, key)
        })
        return {
          ...preset,
          table_columns: [...new Set(tableColumns)]
        }
      })

      commit('SET_PRESETS', presets)
      const defaultPreset = presets.find(preset => preset.is_default)
      const presetToSelect = defaultPreset ?? presets?.[0]
      // If we dont currently have a selected preset, use default
      if (_.isEmpty(state.selectedPreset)) {
        commit('SET_SELECTED_PRESET', presetToSelect)
        commit('SET_LOCAL_PRESET', presetToSelect)
        commit('SET_APPLIED_PRESET', presetToSelect)
      }
    },
    /**
     * -- shouldSelect = true --
     * Used when a user changes presets for a report
     * OR
     * -- shouldSelect = false --
     * Used when a user is swapping between presets for editing
     */
    async setPresetById ({ state, commit }, { id, shouldSelect = false }) {
      const presets = state.presets

      const selectedPreset = (id ? presets.find((preset) => preset.id === id) : presets?.[0]) ?? {}
      // We should keep local and selected state seperate until save/update
      commit('SET_LOCAL_PRESET', selectedPreset)
      if (shouldSelect) {
        commit('SET_SELECTED_PRESET', selectedPreset)
        commit('SET_APPLIED_PRESET', selectedPreset)
      }
    },
    async updatePreset ({ dispatch, state, commit }, toUpdate) {
      const activeLensId = store.getters['LensModule/getActiveLensId']
      const metricLookup = store.getters['MetricsModule/getMetricLookup']

      // Split up the table columns into global and lens specific metrics
      let globalMetrics = []
      let lensSpecificMetrics = []
      toUpdate?.table_columns?.forEach((key, index) => {
        if (metricLookup?.[key]?.is_lens_specific) lensSpecificMetrics.push({ key, index })
        else if (metricLookup?.[key]) globalMetrics.push(key)
      })

      // Ensure uniqueness in both arrays
      globalMetrics = [...new Set(globalMetrics)]
      lensSpecificMetrics = [...new Map(lensSpecificMetrics.map(metric => [metric.key, metric])).values()]

      // Store the selected lens-specific metrics by associating them with the active lens id
      const newLensSpecificMetrics = toUpdate?.lens_specific_columns ?? {}
      if (activeLensId) {
        newLensSpecificMetrics[activeLensId] = lensSpecificMetrics
      }

      // Update the preset with the new metrics
      const newPreset = {
        ...toUpdate,
        table_columns: globalMetrics,
        lens_specific_columns: newLensSpecificMetrics
      }
      try {
        await LensAPI.LensPreset.update(toUpdate.id, newPreset)
      } catch (e) {
        console.error(e)
      }
      const originalPreset = state.localPreset
      commit('SET_LOCAL_PRESET', { ...originalPreset, ...toUpdate })
      await dispatch('fetchPresets')
    },
    async createPreset ({ dispatch }, toSave) {
      const activeLensId = store.getters['LensModule/getActiveLensId']
      const metricLookup = store.getters['MetricsModule/getMetricLookup']

      // Split up the table columns into global and lens specific metrics
      let globalMetrics = []
      let lensSpecificMetrics = []
      toSave?.table_columns?.forEach((key, index) => {
        if (metricLookup?.[key]?.is_lens_specific) lensSpecificMetrics.push({ key, index })
        else if (metricLookup?.[key]) globalMetrics.push(key)
      })

      // Ensure uniqueness in both arrays
      globalMetrics = [...new Set(globalMetrics)]
      lensSpecificMetrics = [...new Map(lensSpecificMetrics.map(metric => [metric.key, metric])).values()]

      const createPayload = {
        ...toSave,
        table_columns: globalMetrics
      }
      if (lensSpecificMetrics.length) {
        createPayload.lens_specific_columns = { [activeLensId]: lensSpecificMetrics }
      }

      try {
        const { data } = await LensAPI.LensPreset.create(createPayload)
        await dispatch('fetchPresets')
        return data.id
      } catch (e) {
        console.error(e)
        return null
      }
    },
    applyPreset ({ state, commit }, toUpdate) {
      commit('SET_APPLIED_PRESET', toUpdate)
      commit('SET_LOCAL_PRESET', toUpdate)
    },
    updatePresetField ({ commit }, { key, value }) {
      commit('SET_PRESET_VALUE', { key, value })
    },
    updateLocalPresetField ({ commit }, { key, value }) {
      commit('SET_LOCAL_PRESET_VALUE', { key, value })
    },
    updateIsCustomPreset ({ commit }, isCustom) {
      commit('SET_IS_CUSTOM_PRESET', isCustom)
    },
    updateColorFormat ({ commit }, key) {
      commit('SET_COLOR_FORMAT', key)
    },
    updatePagination ({ commit }, pagination) {
      commit('SET_PAGINATION', pagination)
    },
    async fetchLenses ({ commit }) {
      try {
        // FETCH LENSES
        const { data: lensData } = await LensAPI.Lens.getAll()
        commit('SET_LENSES', lensData)

        // CHECK FB CONNECTION STATUS
        console.log('%c Fetching Facebook Connection Status', 'font-size: 14px; color: blue')
        const { data: connectionData } = await LensAPI.Lens.getAccountStatus()

        const adAccounts = new Map()
        const { isConnected, isBackfill } = connectionData?.connections?.reduce(
          (acc, account) => {
            const status = account.providerStatus.status
            acc.isConnected = acc.isConnected || status === 'connected' || status === 'backfill'
            acc.isBackfill = acc.isBackfill || status === 'backfill'

            // Store all ad accounts for lookup when checking lens connections
            account.integrations.forEach(adAccount => {
              adAccounts.set(adAccount.providerAccount, { ...adAccount, providerId: account.providerId })
            })

            return acc
          },
          { isConnected: false, isBackfill: false }
        )
        const user = await FirebaseAPI.Users.getById(connectionData?.created_by)
        const status = {
          isConnected: isConnected || isBackfill,
          isBackfill: isBackfill,
          connectionOwner: {
            name: user?.name || `${user?.first_name} ${user?.last_name}`,
            email: user.email,
            avatar: user?.avatar,
            user_id: connectionData?.created_by
          }
        }
        commit('SET_FB_CONNECTION_STATUS', status)

        // CHECK PER LENS AD ACCOUNT CONNECTION STATUS
        const lensConnectionsStatus = {}
        lensData.forEach(lens => {
          const accountIds = lens.ad_accounts.map(account => account.account_id)

          let lensIsConnected = true
          let lensIsBackfill = false
          const providerStatuses = {}
          for (const accountId of accountIds) {
            const adAccount = adAccounts.get(accountId)
            const accountIsConnected = !!(adAccount) // Check if we have the lens's ad account in our connections
            const accountIsBackfill = adAccount?.status === 'backfill'

            // Check if we need to toggle overall lens connections status
            if (!accountIsConnected) {
              lensIsConnected = false
              continue
            }
            if (accountIsBackfill) lensIsBackfill = true

            // Update the provider-specific statuses for this lens (assumes one account per provider in lenses)
            providerStatuses[adAccount.providerId] = {
              isConnected: accountIsConnected,
              isBackfill: accountIsBackfill
            }
          }

          lensConnectionsStatus[lens.id] = {
            isConnected: lensIsConnected,
            isBackfill: lensIsBackfill,
            providerStatuses
          }
        })
        commit('SET_LENS_CONNECTIONS_STATUS', lensConnectionsStatus)
      } catch (e) {
        console.error('Error fetching lenses:', e)
        commit('SET_LENSES', [])
      }
    },
    async fetchSidebarLenses ({ commit }) {
      const computeIsDataReady = (lens) => {
        if (!lens?.accounts?.length) return { isDataReady: false, isDisconnected: true }
        for (const account of lens?.accounts ?? []) {
          console.log('account max event date', account.max_event_date)
          if (account.status === 'ready') {
            return { isDataReady: true, isDisconnected: false }
          } else if (
            account.status === 'backfill' &&
            (
              account.currentIndex === account.total ||
              (account.currentIndex > 7 && isWithinDaysAgo(new Date(account.max_event_date), 7))
            )) {
            return { isDataReady: true, isDisconnected: false }
          }
        }
        return { isDataReady: false, isDisconnected: false }
      }

      try {
        const res = await LensAPI.Lens.getAllSidebar()
        const lensFolders = {}
        res.data.forEach(lens => {
          const folders = [...lens.folders].sort((a, _) => a.name === 'Default' ? -1 : 0)
          lensFolders[lens.id] = folders
        })
        commit('SET_SIDEBAR_LENSES', res.data.map(lens => ({ ...lens, ...computeIsDataReady(lens) })))
        commit('SET_LENS_FOLDERS', lensFolders)
      } catch (e) {
        console.error('Error fetching sidebar lenses:', e)
        commit('SET_SIDEBAR_LENSES', [])
        commit('SET_LENS_FOLDERS', {})
      }
    },
    // TODO @Sam: clean up
    updateRowColor ({ state, commit }, { keys, isAdding, graph, ignorePrevColors }) {
      if (graph === 'grid') return
      const mapCopy = { ...state.mappedRowColors }

      const usedColors = Object.values(mapCopy[graph])
      const lineColorCopy = [...lineColors]
      const unusedColors = ignorePrevColors ? lineColorCopy : lineColorCopy.filter((col) => !usedColors.includes(col))
      // If applying to multiple (0 or > 1)
      if (keys.length !== 1) {
        const newMappedColors = !ignorePrevColors && keys.length > 0 ? { ...state.mappedRowColors[graph] } : {}
        if (keys.length > 0) {
          if (isAdding) {
            keys.forEach((row) => {
              if (!newMappedColors[row.group_by]) {
                newMappedColors[row.group_by] = unusedColors.shift()
              }
            })
          } else {
            Object.keys(newMappedColors).forEach((key) => {
              if (keys.length === 0 || !keys.some((row) => row.group_by === key)) {
                delete newMappedColors[key]
              }
            })
          }
        }
        commit('SET_MAPPED_ROW_COLOR', { newMappedColors, graph })
      } else {
        // Applying to single checkbox
        const key = keys[0]
        if (isAdding) {
          mapCopy[graph][key] = unusedColors[0]
        } else {
          const { [key]: _, ...rest } = mapCopy[graph]
          mapCopy[graph] = { ...rest }
        }
        commit('SET_MAPPED_ROW_COLOR', { newMappedColors: mapCopy })
      }
    },
    async fetchTags ({ state, commit }, lensId = null) {
      try {
        console.log('%c Fetching Tags', 'font-size: 14px; color: pink')
        const reqLensId = lensId ?? (state.activeLensId ?? null)
        const { data } = await LensAPI.Tags.getAll(reqLensId)
        const lensTags = data.map(tag => ({
          ...tag,
          color: tag.color || 'gray'
        }))
        commit('SET_TAGS', lensTags)
      } catch (error) {
        console.error('Error fetching tags', error)
      }
    }
  }
}
